import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Close, Save, Send, Visibility } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { FilesData } from 'services/dataService';
import NewsletterData from 'services/dataService/NewsletterData';
import {
  NEWSLETTER_STATUS,
  isNewsletterContentEmpty,
} from 'helpers/newsletter';
import Editor from '../Editor';
import Post from '../Post';
import { useStyles } from './styles';

export default () => {
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation(['newsletter', 'button', 'toastify']);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [cover, setCover] = useState(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [idFileCover, setIdFileCover] = useState(null);
  const [subplatforms, setSubplatforms] = useState([]);
  const [selected, setSelected] = useState([]);

  const coverPreview = useMemo(
    () => (cover ? URL.createObjectURL(cover) : coverUrl),
    [cover, coverUrl]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesResponse, newsletterResponse] = await Promise.all([
          NewsletterData.getSubplatforms(),
          id ? NewsletterData.getNewsletterById(id) : null,
        ]);

        setSubplatforms(companiesResponse.data.companies);

        if (newsletterResponse) {
          const { data } = newsletterResponse;

          if (!data.can_edit) {
            history.push(`/newsletter/${id}`);
            return;
          }

          setTitle(data.title);
          setContent(data.content || '');
          setCoverUrl(data.cover?.url || '');
          setIdFileCover(data.cover?.id || null);
          setSelected(data.companies || []);
        }

        setLoading(false);
      } catch (err) {
        history.push('/dashboard');
        if (err.response?.status === 403) {
          toast.error(t('toastify:commons.toast_error_403'));
        } else {
          toast.error(t('toastify:commons.toast_error_api'));
        }
      }
    };

    fetchData();
  }, [id]);

  const validate = (status) => {
    const newErrors = {};

    if (!title.trim()) newErrors.title = t('newsletter:validation.title');

    if (status === NEWSLETTER_STATUS.published) {
      if (isNewsletterContentEmpty(content)) {
        newErrors.content = t('newsletter:validation.content');
      }
      if (!selected.length) {
        newErrors.companies = t('newsletter:validation.companies');
      }
    }

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSave = async (status) => {
    if (!validate(status)) return;

    try {
      setSaving(true);

      let id_file_cover = idFileCover;
      if (cover) {
        const responseCover = await FilesData.addFile(cover, 'Newsletter/Cover');
        id_file_cover = responseCover.data.id;
        setIdFileCover(id_file_cover);
        setCoverUrl(responseCover.data.url);
        setCover(null);
      }

      const payload = {
        title: title.trim(),
        content,
        id_file_cover,
        status,
        companies: selected.map((company) => company.id),
      };

      const response = id
        ? await NewsletterData.updateNewsletter(id, payload)
        : await NewsletterData.addNewsletter(payload);

      toast.success(
        status === NEWSLETTER_STATUS.published
          ? t('newsletter:toast.published')
          : t('newsletter:toast.saved_draft')
      );
      history.push(`/newsletter/${response.data.id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t('toastify:commons.toast_error_403'));
      } else {
        toast.error(t('newsletter:toast.save_error'));
      }
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader
        title={
          id
            ? t('newsletter:form.title_edit')
            : t('newsletter:form.title_create')
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('newsletter:form.title')}
              variant="outlined"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              inputProps={{ maxLength: 255 }}
              InputLabelProps={{ shrink: true }}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {t('newsletter:form.cover')}
            </Typography>
            {coverPreview && (
              <Box className={classes.cover}>
                <img src={coverPreview} alt="" />
              </Box>
            )}
            <input
              style={{ display: 'none' }}
              type="file"
              id="newsletter-cover-file"
              accept="image/png, image/jpeg"
              onChange={(event) => {
                if (event.target.files[0]) setCover(event.target.files[0]);
                event.target.value = '';
              }}
            />
            <label htmlFor="newsletter-cover-file">
              <Button variant="contained" color="primary" component="span">
                {coverPreview
                  ? t('newsletter:form.cover_change')
                  : t('newsletter:form.cover_select')}
              </Button>
            </label>
            {coverPreview && (
              <Button
                className={classes.coverRemove}
                onClick={() => {
                  setCover(null);
                  setCoverUrl('');
                  setIdFileCover(null);
                }}
              >
                {t('newsletter:form.cover_remove')}
              </Button>
            )}
            <FormHelperText>{t('newsletter:form.cover_hint')}</FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {t('newsletter:form.content')}
            </Typography>
            <Editor value={content} onChange={setContent} />
            {errors.content && (
              <FormHelperText error>{errors.content}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={6}
              options={subplatforms}
              value={selected}
              onChange={(event, value) => setSelected(value)}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderOption={(option, { selected: checked }) => (
                <>
                  <Checkbox color="primary" checked={checked} />
                  {option.name}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('newsletter:form.companies')}
                  placeholder={t('newsletter:form.companies_placeholder')}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.companies}
                  helperText={errors.companies}
                />
              )}
            />
            <Box mt={1}>
              <Button
                size="small"
                color="primary"
                onClick={() => setSelected(subplatforms)}
              >
                {t('newsletter:form.select_all')}
              </Button>
              <Button size="small" onClick={() => setSelected([])}>
                {t('newsletter:form.clear')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardActions className={classes.actions}>
        <Button disabled={saving} onClick={() => history.push('/dashboard')}>
          {t('button:commons.btn_cancel')}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          startIcon={<Visibility />}
          onClick={() => setPreviewOpen(true)}
        >
          {t('newsletter:form.btn_preview')}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          disabled={saving}
          startIcon={<Save />}
          onClick={() => handleSave(NEWSLETTER_STATUS.draft)}
        >
          {t('newsletter:form.btn_draft')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={saving}
          startIcon={<Send />}
          onClick={() => handleSave(NEWSLETTER_STATUS.published)}
        >
          {t('newsletter:form.btn_publish')}
        </Button>
      </CardActions>

      <Dialog
        fullScreen
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      >
        <AppBar className={classes.previewBar}>
          <Toolbar>
            <Typography className={classes.previewTitle} variant="h5">
              {t('newsletter:preview.title')}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              aria-label={t('newsletter:preview.close')}
              onClick={() => setPreviewOpen(false)}
            >
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent className={classes.previewContent}>
          <Post
            title={title.trim() || t('newsletter:preview.empty_title')}
            coverUrl={coverPreview}
            content={content}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};
